<script lang="ts">
  import '$features/workspace/styles/auth-guest.css';
  import { signIn } from '../stores/auth';
  import SocialLoginButtons from './SocialLoginButtons.svelte';
  import { loginSchema, firstFieldErrors, getCognitoErrorMessage, type LoginFormData } from '../schemas/auth';
  import { focusFirstInvalid, restoreFocus } from '../focus';
  import { goto } from '$app/navigation';
  import { navigateAfterAuth } from '$features/onboarding';
  import { Alert, Button } from '$features/workspace/ui';
  import { CircleAlert } from 'lucide-svelte';
  import { onMount } from 'svelte';
  import { getConfigOrNull, loadConfig } from '$config';

  // Props
  let { redirectTo = '/' }: { redirectTo?: string } = $props();

  const signupHref = $derived(
    redirectTo && redirectTo !== '/' ? `/signup?redirect=${encodeURIComponent(redirectTo)}` : '/signup'
  );

  let buildNumber = $state(getConfigOrNull()?.build?.number ?? '');
  const showBuild = $derived(!!buildNumber && buildNumber !== '{BUILD_NUMBER}');

  onMount(async () => {
    if (buildNumber) return;
    try {
      const config = getConfigOrNull() ?? (await loadConfig());
      buildNumber = config.build?.number ?? '';
    } catch {
      // Config unavailable — leave the build line hidden.
    }
  });

  // Form state
  let formData = $state<LoginFormData>({
    email: '',
    password: '',
  });

  let errors = $state<Partial<Record<keyof LoginFormData, string>>>({});
  let generalError = $state<string>('');
  let isSubmitting = $state(false);
  let form: HTMLFormElement | null = $state(null);

  // Validation
  function validateForm(): boolean {
    const result = loginSchema.safeParse(formData);
    errors = result.success ? {} : firstFieldErrors(result.error);
    return result.success;
  }

  // Handle form submission
  async function handleSubmit(event: Event) {
    event.preventDefault();
    if (isSubmitting) return;
    generalError = '';

    if (!validateForm()) {
      void focusFirstInvalid(form);
      return;
    }

    const returnFocus = document.activeElement;
    isSubmitting = true;

    try {
      await signIn(formData.email, formData.password);
      if (redirectTo && redirectTo !== '/') {
        await goto(redirectTo);
      } else {
        await navigateAfterAuth();
      }
    } catch (error: any) {
      console.error('Login error:', error);
      generalError = getCognitoErrorMessage(error);
    } finally {
      isSubmitting = false;
      void restoreFocus(returnFocus);
    }
  }

  // Clear field error on input
  function clearError(field: keyof LoginFormData) {
    errors[field] = undefined;
    generalError = '';
  }
</script>

<form method="POST" onsubmit={handleSubmit} class="ag-auth__form" novalidate bind:this={form}>
  {#if generalError}
    <Alert tone="error" live="assertive">{generalError}</Alert>
  {/if}

  <div class="ag-auth__fields">
    <div class="stat-field">
      <label class="stat-field__label" for="username">Email</label>
      <input
        id="username"
        name="username"
        type="email"
        class="stat-input"
        placeholder="name@example.com"
        autocomplete="username webauthn"
        autocapitalize="none"
        spellcheck="false"
        inputmode="email"
        required
        disabled={isSubmitting}
        bind:value={formData.email}
        oninput={() => clearError('email')}
        aria-invalid={!!errors.email}
        aria-describedby={errors.email ? 'email-error' : undefined}
      />
      {#if errors.email}
        <p id="email-error" class="stat-field__error">
          <CircleAlert size={16} aria-hidden="true" />
          <span>{errors.email}</span>
        </p>
      {/if}
    </div>

    <div class="stat-field">
      <div class="ag-field__row">
        <label class="stat-field__label" for="password">Password</label>
        <a href="/forgot-password" class="stat-text-action" tabindex={isSubmitting ? -1 : 0}>Forgot password?</a>
      </div>
      <input
        id="password"
        name="password"
        type="password"
        class="stat-input"
        placeholder="••••••••"
        autocomplete="current-password"
        required
        disabled={isSubmitting}
        bind:value={formData.password}
        oninput={() => clearError('password')}
        aria-invalid={!!errors.password}
        aria-describedby={errors.password ? 'password-error' : undefined}
      />
      {#if errors.password}
        <p id="password-error" class="stat-field__error">
          <CircleAlert size={16} aria-hidden="true" />
          <span>{errors.password}</span>
        </p>
      {/if}
    </div>
  </div>

  <Button type="submit" variant="primary" block pending={isSubmitting} pendingLabel="Signing in…">Sign in</Button>

  <SocialLoginButtons disabled={isSubmitting} onerror={(message) => (generalError = message)} />

  <p class="ag-auth__footer">
    Don’t have an account?
    <a href={signupHref} class="ag-link">Create an account</a>
  </p>
  {#if showBuild}
    <p class="ag-auth__meta">Build: {buildNumber}</p>
  {/if}
</form>
